/*
 * Kroslo Build Your Bundle: site-wide cart sync + floating bundle bar (prefix kbx).
 *
 * Rules this file enforces:
 *  1. The Shopify cart (/cart.js) is the only record of what's in the bundle. We never keep our own copy.
 *  2. Any cart change from anywhere (theme, Shopflo drawer, this bar, another tab) triggers a re-read.
 *  3. Only one thing sits at the bottom of the screen. The bar hides while any drawer, modal,
 *     pop-up or checkout is open, and it replaces the theme's sticky add-to-cart bar
 *     on product pages once the bundle has started.
 *  4. Fail-safe: on the LIVE theme, if prices are not yet 80% of MRP (festive pricing
 *     not switched on), the bar does not render, so it can never promise a price checkout won't charge.
 *
 * Public API (used by the Build Your Bundle page): window.KBX
 */
(function () {
  "use strict";
  if (window.KBX) return;
  var E = window.KBXEngine;
  var cfgEl = document.getElementById("kbx-config");
  if (!E || !cfgEl) return;
  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (err) { return; }

  var TIERS = E.normalizeTiers(CFG.tiers);
  var SLOTS = TIERS[TIERS.length - 1].min;
  var PAGE = CFG.page || {};
  var FLO_ORIGIN = "https://checkout.shopflo.co";
  var SHOW_ON = { index: 1, collection: 1, product: 1, page: 1, search: 1, "list-collections": 1, blog: 1, article: 1 };
  var fmt = E.formatINR;

  /* ---------- catalogue lookups ---------- */
  var VARIANTS = {}, PRODUCTS = [], SETS = {};
  (CFG.products || []).forEach(function (p) {
    if (!p || !p.v || !p.v.length) return;
    PRODUCTS.push(p);
    p.v.forEach(function (v) { v.product = p; VARIANTS[v.id] = v; });
  });
  (CFG.sets || []).forEach(function (id) { SETS[id] = true; });

  /* Festive pricing is live once every eligible variant costs exactly 80% of MRP. */
  var PRICING_LIVE = PRODUCTS.length > 0 && Object.keys(VARIANTS).every(function (id) {
    var v = VARIANTS[id];
    return v.p === E.unitAtPct(v.m, TIERS[0].pct);
  });
  var ROLE = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || "";
  var IS_PREVIEW = ROLE !== "main";
  var FAILSAFE_OFF = !IS_PREVIEW && !PRICING_LIVE;

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function track(event, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var o = { event: event };
      for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) o[k] = data[k];
      window.dataLayer.push(o);
    } catch (e) {}
  }

  /* =====================================================================
     1. CART STORE: single source of truth
     ===================================================================== */
  var Store = { cart: null, loaded: false, seq: 0, applied: 0, pending: 0, timer: null, subs: [], queue: Promise.resolve() };

  /* Only a FULL cart counts: /cart/add.js replies with just the added items. */
  function isFullCart(c) {
    return !!(c && Array.isArray(c.items) && typeof c.item_count === "number" && typeof c.token === "string");
  }
  function applyCart(cart, seq) {
    if (!isFullCart(cart)) return;
    if (seq < Store.applied) return;           // an older response arrived late: ignore it
    Store.applied = seq;
    Store.cart = cart;
    Store.loaded = true;
    var d = derive(cart);
    Store.subs.forEach(function (fn) { try { fn(d); } catch (e) { if (window.console) console.error("[KBX]", e); } });
  }
  Store.load = function () {
    /* A read that starts while one of our writes is saving can return the old cart. Wait for the write. */
    if (Store.pending) {
      Store.queue.then(function () { Store.refresh(0); });
      return Promise.resolve(Store.cart);
    }
    var seq = ++Store.seq;
    return window.fetch("/cart.js", {
      credentials: "same-origin", cache: "no-store",
      headers: { Accept: "application/json", "X-KBX": "1" }
    }).then(function (r) {
      if (!r.ok) throw new Error("cart " + r.status);
      return r.json();
    }).then(function (cart) { applyCart(cart, seq); return Store.cart; })
      .catch(function () { return Store.cart; });
  };
  Store.refresh = function (delay) {
    clearTimeout(Store.timer);
    Store.timer = setTimeout(Store.load, delay == null ? 120 : delay);
  };
  Store.subscribe = function (fn) {
    Store.subs.push(fn);
    if (Store.loaded) fn(derive(Store.cart));
  };
  /* Serialised cart writes: one at a time, in order. Double taps can't race. */
  Store.mutate = function (path, payload) {
    var run = function () {
      Store.pending++;
      return window.fetch(path, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-KBX": "1" },
        body: JSON.stringify(payload)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          Store.pending--;
          var seq = ++Store.seq;   // numbered on arrival, so any read that started earlier counts as older
          if (r.ok && isFullCart(data)) applyCart(data, seq);
          else Store.refresh(0);
          return { ok: r.ok, status: r.status, data: data };
        });
      }, function () {
        Store.pending--;
        Store.refresh(0);
        return { ok: false, status: 0, data: { description: "Network problem. Please try again." } };
      });
    };
    var p = Store.queue.then(run, run);
    Store.queue = p.then(function () {}, function () {});
    return p;
  };

  function derive(cart) {
    var lines = [], sets = 0;
    ((cart && cart.items) || []).forEach(function (it) {
      var v = VARIANTS[it.variant_id];
      if (v) {
        lines.push({ key: it.key, vid: it.variant_id, qty: it.quantity, mrp: v.m, price: it.original_price, item: it, v: v });
      } else if (SETS[it.variant_id]) {
        sets += it.quantity;
      }
    });
    var r = E.compute(lines, TIERS);
    r.src = lines;
    r.setCount = sets;
    r.cartCount = (cart && cart.item_count) || 0;
    return r;
  }
  Store.current = function () { return derive(Store.cart); };

  /* Notice cart writes made by anyone (theme, Shopflo, apps). */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  (function patchFetch() {
    var orig = window.fetch;
    if (!orig) return;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var method = ((init && init.method) || (input && input.method) || "GET").toUpperCase();
      var headers = (init && init.headers) || {};
      var mine = headers["X-KBX"] === "1";
      var p = orig.apply(this, arguments);
      if (method === "POST" && CART_WRITE.test(url)) {
        if (/\/cart\/add/.test(url)) Watch.holdForDrawer();
        if (!mine) p.then(function () { Store.refresh(80); }, function () { Store.refresh(80); });
      }
      return p;
    };
  })();
  (function patchXHR() {
    var X = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!X) return;
    var open = X.open, send = X.send;
    X.open = function (method, url) { this.__kbx = { m: String(method || "").toUpperCase(), u: String(url || "") }; return open.apply(this, arguments); };
    X.send = function () {
      var info = this.__kbx;
      if (info && info.m === "POST" && CART_WRITE.test(info.u)) {
        if (/\/cart\/add/.test(info.u)) Watch.holdForDrawer();
        this.addEventListener("loadend", function () { Store.refresh(80); });
      }
      return send.apply(this, arguments);
    };
  })();

  /* Shopflo's cart/checkout iframe tells the page when its cart changes. */
  window.addEventListener("message", function (e) {
    if (e.origin !== FLO_ORIGIN || !e.data || typeof e.data !== "object") return;
    var t = e.data.type;
    if (typeof t === "string" && /CART|ITEM/.test(t) && t !== "CART_API_SUCCESS") Store.refresh(150);
    Watch.check();
  });
  /* Back button (bfcache), switching tabs, coming back to the window. */
  window.addEventListener("pageshow", function (e) { if (e.persisted) Store.refresh(0); });
  document.addEventListener("visibilitychange", function () { if (document.visibilityState === "visible") Store.refresh(0); });
  window.addEventListener("focus", function () { Store.refresh(200); });

  /* =====================================================================
     2. OVERLAY WATCHER: hide the bar whenever anything else is on top
     ===================================================================== */
  var Watch = { holdUntil: 0, keyboard: false, open: false, raf: 0, subs: [] };

  function visible(el) {
    if (!el) return false;
    var cs = window.getComputedStyle(el);
    return cs.display !== "none" && cs.visibility !== "hidden" && Number(cs.opacity) > 0.01;
  }
  Watch.isOverlayOpen = function () {
    var b = document.body;
    if (!b) return true;
    if (b.classList.contains("prevent-scroll")) return true;                         // theme drawers, modals, pop-ups
    if (visible(document.getElementById("flo-cart-overlay-div"))) return true;        // Shopflo cart
    if (visible(document.getElementById("flo-checkout-overlay-div"))) return true;    // Shopflo checkout
    if (document.querySelector("drawer-component[open],modal-dialog[open],.f-popup[open]")) return true;
    if (document.querySelector(".f-cookie-banner.show")) return true;
    var kp = document.querySelector(".iframe-kp");                                   // GoKwik KwikPass login
    if (kp && !kp.classList.contains("hidden") && visible(kp)) return true;
    if (document.querySelector("[data-kbx-sheet-open]")) return true;                // Build Your Bundle picker sheet
    return false;
  };
  var COARSE = !!(window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
  function typingNow() {
    if (!COARSE) return false;
    var a = document.activeElement;
    if (!a || !/^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return false;
    if (/^(checkbox|radio|button|submit|range|color|file|hidden)$/i.test(a.type || "")) return false;
    if (a.closest && a.closest(".kbx")) return false;
    return a.isConnected !== false;
  }
  Watch.holdForDrawer = function () {
    Watch.holdUntil = Date.now() + 2500;   // Shopflo opens its drawer about 1.2s after an add
    Watch.check();
    setTimeout(Watch.check, 2600);
  };
  /* A plain timer, not requestAnimationFrame: rAF pauses in background tabs and
     would leave the bar showing over a drawer. */
  Watch.check = function () {
    if (Watch.raf) return;
    Watch.raf = setTimeout(function () {
      Watch.raf = 0;
      var floOpen = visible(document.getElementById("flo-cart-overlay-div")) ||
                    visible(document.getElementById("flo-checkout-overlay-div"));
      if (floOpen) Watch.holdUntil = 0;
      Watch.keyboard = typingNow();
      var open = Watch.keyboard || Date.now() < Watch.holdUntil || Watch.isOverlayOpen();
      if (open !== Watch.open) {
        Watch.open = open;
        Watch.subs.forEach(function (fn) { fn(open); });
      }
    }, 16);
  };
  function startWatching() {
    var mo = new MutationObserver(Watch.check);
    var watched = new WeakSet();
    function attach() {
      mo.observe(document.body, { attributes: true, attributeFilter: ["class", "style"], childList: true });
      var els = document.querySelectorAll(
        "#flo-cart-overlay-div,#flo-checkout-overlay-div,drawer-component,modal-dialog,.f-popup,.f-cookie-banner,.iframe-kp,.flo-cart-wrapper,.flo-checkout-wrapper");
      Array.prototype.forEach.call(els, function (el) {
        if (watched.has(el)) return;
        watched.add(el);
        mo.observe(el, { attributes: true, attributeFilter: ["class", "style", "open", "hidden"], childList: el.classList.contains("flo-cart-wrapper") || el.classList.contains("flo-checkout-wrapper") });
      });
    }
    attach();
    /* Shopflo and pop-up apps inject their elements after load: re-attach when the body changes. */
    new MutationObserver(function () { attach(); Watch.check(); }).observe(document.body, { childList: true });
    /* Safety net, cheap: re-check twice a second while a bar could be visible. */
    setInterval(function () { if (Bar.wanted) Watch.check(); }, 500);
    /* Mobile keyboard pushes fixed bars around: hide while typing. The state is
       re-read from document.activeElement on every check, so it can never get stuck. */
    document.addEventListener("focusin", Watch.check);
    document.addEventListener("focusout", function () { setTimeout(Watch.check, 120); });
    Watch.check();
  }

  /* =====================================================================
     3. ACTIONS
     ===================================================================== */
  var toastEl = null, toastTimer = 0;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "kbx-toast";
      toastEl.setAttribute("role", "status");
      toastEl.setAttribute("aria-live", "polite");
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add("kbx-toast--in");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove("kbx-toast--in"); }, 2800);
  }
  /* handleFloCartBtn is what the theme's own add-to-cart and header cart icon use: it
     reloads the cart before opening. Shopflo.openFloCart() can show a stale copy. */
  function openCart() {
    track("kbx_cta", { kbx_action: "view_cart" });
    if (typeof window.handleFloCartBtn === "function") {
      try { window.handleFloCartBtn(); return; } catch (e) {}
    }
    if (window.Shopflo && typeof window.Shopflo.openFloCart === "function") {
      try { window.Shopflo.openFloCart(); return; } catch (e) {}
    }
    window.location.href = "/cart";
  }
  function checkout(ev) {
    track("kbx_cta", { kbx_action: "checkout" });
    if (typeof window.handleFloCheckoutBtn === "function") {
      try { window.handleFloCheckoutBtn(ev || new Event("click")); return; } catch (e) {}
    }
    openCart();
  }
  function qtyInCart(vid) {
    var q = 0;
    ((Store.cart && Store.cart.items) || []).forEach(function (it) { if (it.variant_id === vid) q += it.quantity; });
    return q;
  }
  /* quiet:true adds without making the Shopflo drawer pop open (used on the Build Your Bundle page). */
  function addVariant(vid, opts) {
    vid = Number(vid);
    var v = VARIANTS[vid];
    if (!v) return Promise.resolve({ ok: false, message: "This piece isn't part of the bundle." });
    if (!v.a || v.q < 1) return Promise.resolve({ ok: false, message: "This size is sold out." });
    var quiet = opts && opts.quiet;
    var request;
    if (quiet) {
      var want = qtyInCart(vid) + 1;
      if (want > v.q) return Promise.resolve({ ok: false, message: "Only " + v.q + " left in stock." });
      var u = {}; u[vid] = want;
      request = Store.mutate("/cart/update.js", { updates: u }).then(function (res) {
        if (res.ok && qtyInCart(vid) < want) return { ok: false, message: "Only " + qtyInCart(vid) + " left in stock." };
        return res;
      });
    } else {
      request = Store.mutate("/cart/add.js", { items: [{ id: vid, quantity: 1 }] });
    }
    return request.then(function (res) {
      if (res.ok) { track("kbx_add", { kbx_vid: vid, kbx_quiet: !!quiet }); return { ok: true }; }
      return { ok: false, message: res.message || (res.data && (res.data.description || res.data.message)) || "Couldn't add this piece. Please try again." };
    });
  }
  function setLineQty(key, qty) {
    return Store.mutate("/cart/change.js", { id: key, quantity: Math.max(0, qty | 0) }).then(function (res) {
      return res.ok ? { ok: true } : { ok: false, message: (res.data && res.data.description) || "Couldn't update your cart." };
    });
  }

  /* =====================================================================
     4. THE BAR
     ===================================================================== */
  var Bar = { el: null, wanted: false, shown: false, mini: false, pref: "full", lastPct: null,
              pendingCelebrate: 0, busy: false, viewed: false };
  var PREF_KEY = "kbx-bar-pref";
  try { Bar.pref = window.sessionStorage.getItem(PREF_KEY) === "mini" ? "mini" : "full"; } catch (e) {}
  Bar.mini = Bar.pref === "mini";

  var CHEVRON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 15 6-6 6 6"/></svg>';

  var CROSS = '<svg width="8" height="8" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

  function currentPdpVariant() {
    var form = document.querySelector("form.main-product-form") ||
               document.querySelector('form[action*="/cart/add"][id^="product-form-template"]');
    var input = form && form.querySelector('[name="id"]');
    var vid = input ? Number(input.value) : 0;
    return VARIANTS[vid] || null;
  }

  function slotThumbs(d) {
    var list = [];
    d.src.forEach(function (l) {
      for (var i = 0; i < l.qty; i++) list.push({ img: l.v.product.i || (l.item && l.item.image) || "", alt: l.v.product.t, key: l.key, qty: l.qty });
    });
    return list;
  }

  function render(d) {
    var el = Bar.el;
    var thumbs = slotThumbs(d);
    var preview = IS_PREVIEW && !PRICING_LIVE ? '<span class="kbx__tag">Preview</span>' : "";
    var title, save, msg;
    if (d.isEmpty) {
      title = "Build your bundle";
      save = "Up to <b>" + TIERS[TIERS.length - 1].pct + "%</b> off MRP";
      msg = "Add your first piece: <b>" + TIERS[0].pct + "% off MRP</b>";
    } else {
      title = d.pieces + (d.pieces === 1 ? " piece" : " pieces") + " · <b>" + d.pct + "% off</b>";
      save = "You save <b>" + fmt(Math.floor(d.savings / 100) * 100) + "</b>";
      if (d.isMax) msg = "Best price unlocked: <b>" + d.pct + "% off MRP</b> on every piece";
      else msg = "Add " + d.toNext + " more → <b>" + d.next.pct + "% off</b> every piece";
    }

    var cells = "";
    for (var i = 0; i < SLOTS; i++) {
      var t = thumbs[i];
      var pct = E.pctFor(TIERS, i + 1);
      var isNext = i === thumbs.length;
      var extra = (i === SLOTS - 1 && thumbs.length > SLOTS) ? thumbs.length - SLOTS + 1 : 0;
      var label = t ? ("Piece " + (i + 1) + ": " + t.alt) : ("Empty slot " + (i + 1) + ", " + pct + "% off MRP");
      cells += '<div class="kbx__cell"><div class="kbx__sw"><button type="button" class="kbx__slot' + (t ? " kbx__slot--on" : "") +
        (isNext ? " kbx__slot--next" : "") + '" data-kbx="slot" aria-label="' + esc(label) + '">' +
        (t && t.img ? '<img src="' + esc(t.img) + '" alt="" width="48" height="48" loading="lazy" decoding="async">' : "") +
        (!t ? '<span class="kbx__plus" aria-hidden="true">+</span>' : "") +
        (extra ? '<span class="kbx__more">+' + extra + "</span>" : "") +
        "</button>" +
        (t && !extra ? '<button type="button" class="kbx__rm" data-kbx="rm" data-key="' + esc(t.key) + '" aria-label="Remove one ' + esc(t.alt) + '">' + CROSS + "</button>" : "") +
        '</div><span class="kbx__cap' + (t ? " kbx__cap--on" : "") + (isNext ? " kbx__cap--next" : "") + '">' +
        pct + "%</span></div>";
    }

    var btns;
    if (PAGE.byob) {
      btns = '<button type="button" class="kbx__btn" data-kbx="checkout"' + (d.cartCount ? "" : " disabled") + ">Checkout</button>";
    } else if (PAGE.tpl === "product" && PAGE.pe) {
      var cv = currentPdpVariant();
      var soldOut = cv && (!cv.a || cv.q < 1);
      var addLabel = "Add this";
      if (cv && !soldOut) addLabel = "Add this · " + fmt(Math.ceil(E.unitAtPct(cv.m, E.pctFor(TIERS, d.pieces + 1)) / 100) * 100);
      btns = '<button type="button" class="kbx__btn kbx__btn--alt" data-kbx="cart">Cart</button>' +
             '<button type="button" class="kbx__btn" data-kbx="add"' + (soldOut ? " disabled" : "") +
             (Bar.busy ? ' aria-busy="true"' : "") + ">" + (soldOut ? "Sold out" : esc(addLabel)) + "</button>";
    } else {
      btns = '<button type="button" class="kbx__btn" data-kbx="cart">View cart</button>';
    }

    el.innerHTML =
      '<div class="kbx__head">' +
        '<button type="button" class="kbx__toggle" data-kbx="toggle" aria-expanded="' + (!Bar.mini) + '" aria-label="' +
          (Bar.mini ? "Show bundle details" : "Hide bundle details") + '">' +
          '<span class="kbx__title">' + title + "</span>" + preview + '<span class="kbx__save">' + save + '</span>' +
          '<span class="kbx__chev">' + CHEVRON + "</span></button>" +
      "</div>" +
      '<div class="kbx__slots">' + cells + "</div>" +
      '<div class="kbx__foot' + (PAGE.tpl === "product" && PAGE.pe ? " kbx__foot--two" : "") + '"><span class="kbx__msg" aria-live="polite">' + msg + '</span><span class="kbx__btns">' + btns + "</span></div>" +
      (d.setCount ? '<div class="kbx__note">Sets are specially priced and don\'t count towards your bundle.</div>' : "");

    if (Bar.lastPct !== null && d.pct > Bar.lastPct && d.pieces > 1) {
      track("kbx_tier_up", { kbx_pieces: d.pieces, kbx_pct: d.pct });
      Bar.pendingCelebrate = d.pct;           // played the next time the bar is visible
    }
    Bar.lastPct = d.pct;
    if (Bar.shown) celebrate();
  }

  /* Tier unlocked: a short highlight, plus a toast off the bundle page (the page shows its own). */
  function celebrate() {
    var pct = Bar.pendingCelebrate;
    if (!pct || !Bar.shown) return;
    Bar.pendingCelebrate = 0;
    Bar.el.classList.remove("kbx--up");
    void Bar.el.offsetWidth;
    Bar.el.classList.add("kbx--up");
    setTimeout(function () { Bar.el.classList.remove("kbx--up"); }, 1600);
    if (!PAGE.byob) toast(pct + "% off MRP unlocked on every piece");
  }

  function wantBar(d) {
    if (FAILSAFE_OFF || !Store.loaded) return false;
    if (!SHOW_ON[PAGE.tpl]) return false;
    if (PAGE.byob) return true;
    if (d.pieces < 1) return false;
    if (PAGE.tpl === "product" && !PAGE.pe) return false;   // a set's product page keeps its own add-to-cart bar
    return true;
  }

  function sync() {
    if (!Bar.el) return;
    var d = Store.current();
    Bar.wanted = wantBar(d);
    /* Hand the bottom edge over from the theme's sticky add-to-cart bar */
    document.body.classList.toggle("kbx-dock", Bar.wanted && PAGE.tpl === "product");
    var show = Bar.wanted && !Watch.open;
    if (show !== Bar.shown) {
      Bar.shown = show;
      Bar.el.classList.toggle("kbx--in", show);
      Bar.el.setAttribute("aria-hidden", show ? "false" : "true");
      if ("inert" in Bar.el) Bar.el.inert = !show;
      if (show && !Bar.viewed) { Bar.viewed = true; track("kbx_bar_view", { kbx_pieces: d.pieces, kbx_pct: d.pct, kbx_page: PAGE.tpl }); }
      if (show) setTimeout(celebrate, 320);
    }
    measure();
  }
  /* Shopify's "Draft" preview strip covers the bottom 64px in theme preview only. */
  function previewOffset() {
    var off = IS_PREVIEW && document.getElementById("PBarNextFrame") ? "64px" : "0px";
    if (Bar.el.style.getPropertyValue("--kbx-pb") !== off) Bar.el.style.setProperty("--kbx-pb", off);
  }
  function barTakesSpace() {
    /* On desktop the bar floats in the corner (and the bundle page has its own summary), so it pads nothing. */
    /* (offsetParent is always null for position:fixed, so check display instead) */
    return Bar.shown && window.getComputedStyle(Bar.el).display !== "none" &&
      !(window.matchMedia && window.matchMedia("(min-width: 990px)").matches);
  }
  function measure() {
    if (!Bar.el) return;
    previewOffset();
    if (barTakesSpace()) {
      document.body.style.setProperty("--kbx-h", Math.ceil(Bar.el.getBoundingClientRect().height) + "px");
      document.body.classList.add("kbx-on");
    } else {
      document.body.classList.remove("kbx-on");
      document.body.style.setProperty("--kbx-h", "0px");
    }
  }
  function setMini(mini) {
    if (mini === Bar.mini) return;
    Bar.mini = mini;
    Bar.el.classList.toggle("kbx--mini", mini);
    var t = Bar.el.querySelector(".kbx__toggle");
    if (t) {
      t.setAttribute("aria-expanded", String(!mini));
      t.setAttribute("aria-label", mini ? "Show bundle details" : "Hide bundle details");
    }
    measure();
  }

  function onBarClick(e) {
    var btn = e.target.closest && e.target.closest("[data-kbx]");
    if (!btn || btn.disabled) return;
    var what = btn.getAttribute("data-kbx");
    if (what === "toggle") {
      Bar.pref = Bar.mini ? "full" : "mini";
      try { window.sessionStorage.setItem(PREF_KEY, Bar.pref); } catch (err) {}
      setMini(Bar.pref === "mini");
      return;
    }
    if (what === "cart" || (what === "slot" && !PAGE.byob)) { openCart(); return; }
    if (what === "slot" && PAGE.byob) { document.dispatchEvent(new CustomEvent("kbx:open-picker")); return; }
    if (what === "checkout") { checkout(e); return; }
    if (what === "rm") {
      if (Bar.busy) return;
      var key = btn.getAttribute("data-key");
      var line = Store.current().src.filter(function (l) { return l.key === key; })[0];
      if (!line) return;
      Bar.busy = true;
      setLineQty(key, line.qty - 1).then(function (res) {
        Bar.busy = false;
        if (!res.ok) toast(res.message);
        render(Store.current());
        sync();
      });
      return;
    }
    if (what === "add") {
      if (Bar.busy) return;
      var v = currentPdpVariant();
      if (!v) { toast("Please choose a size first."); return; }
      Bar.busy = true;
      btn.setAttribute("aria-busy", "true");
      addVariant(v.id).then(function (res) {
        Bar.busy = false;
        if (!res.ok) toast(res.message);
        else openCart();   // same as the theme's add-to-cart: show the Shopflo drawer
        render(Store.current());
        sync();
      });
    }
  }

  /* Scrolling down shrinks the bar to its header; scrolling up restores the customer's choice. */
  function setupScrollMini() {
    if (PAGE.byob) return;
    var lastY = window.scrollY, ticking = false;
    window.addEventListener("scroll", function () {
      if (ticking) return;
      ticking = true;
      setTimeout(function () {
        ticking = false;
        var y = window.scrollY;
        if (y > lastY + 12 && y > 240) setMini(true);
        else if (y < lastY - 12 || y < 120) setMini(Bar.pref === "mini");
        if (Math.abs(y - lastY) > 12) lastY = y;
      }, 50);
    }, { passive: true });
  }

  function init() {
    Bar.el = document.createElement("div");
    Bar.el.id = "kbx-bar";
    Bar.el.className = "kbx" + (PAGE.byob ? " kbx--byob" : "") + (Bar.mini ? " kbx--mini" : "");
    Bar.el.setAttribute("role", "region");
    Bar.el.setAttribute("aria-label", "Your bundle");
    Bar.el.setAttribute("aria-hidden", "true");
    if ("inert" in Bar.el) Bar.el.inert = true;
    document.body.appendChild(Bar.el);
    Bar.el.addEventListener("click", onBarClick);

    Store.subscribe(function (d) { render(d); sync(); });
    Watch.subs.push(function () { sync(); });

    if (PAGE.tpl === "product" && PAGE.pe) {
      /* Size changes on the product page: refresh the "Add this" price / "Sold out". */
      document.addEventListener("change", function (e) {
        if (e.target && e.target.closest && e.target.closest("form.main-product-form, variant-radios, variant-selects, .f-product-single")) {
          setTimeout(function () { if (Store.loaded) render(Store.current()); }, 60);
        }
      });
    }
    window.addEventListener("resize", measure, { passive: true });
    setupScrollMini();
    startWatching();
    Store.load();
  }

  window.KBX = {
    version: "1.0.0",
    engine: E,
    tiers: TIERS,
    config: CFG,
    variants: VARIANTS,
    products: PRODUCTS,
    pricingLive: PRICING_LIVE,
    isPreview: IS_PREVIEW,
    store: Store,
    watch: Watch,
    current: function () { return Store.current(); },
    subscribe: Store.subscribe,
    addVariant: addVariant,
    setLineQty: setLineQty,
    openCart: openCart,
    checkout: checkout,
    toast: toast,
    fmt: fmt,
    esc: esc
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
